import Cookies from 'js-cookie';
import { ApiService } from '../api';
import { URLS, UrlUtils } from '../urls';
import { ACCESS_TOKEN_KEY } from '../../shared/constants';
import { WebUtils } from '../../utils/web_utils';
import { L10nUtils } from '../../utils/l10n_utils';
// ---------------------------------------------------------------------------
// Cross-domain OAuth contract.
//
// The provider selector and callbacks live on a single central auth domain
// (URLS.OAUTH_AUTH_ORIGIN), while the viewer runs on many client domains.
// Because the session created on the auth domain is not readable here, the
// token is handed back through a UUID-correlated postMessage relay:
//
//   1. we mint a UUID and store it in a first-party cookie on this domain,
//   2. we redirect the top-level page to `/sign_in`, passing this page as the
//      return URL and the UUID as `state`,
//   3. the auth domain runs the provider flow, mints a token keyed by `state`,
//      and redirects back to our return URL (marked with OAUTH_CALLBACK_PARAM),
//   4. on return we load the auth domain's token page in a hidden iframe with
//      the same `state`; it posts the token back to us via postMessage.
//
// The names below are the wire contract with the backend — keep them in sync.
// ---------------------------------------------------------------------------
const OAUTH_STATE_COOKIE = 'sphere_oauth_state';
const REDIRECT_URI_PARAM = 'redirect_uri';
const STATE_PARAM = 'state';
const OAUTH_CALLBACK_PARAM = 'sphere_oauth';
const OAUTH_CALLBACK_VALUE = 'callback';
const TOKEN_MESSAGE_TYPE = 'sphere-oauth-token';
const TOKEN_RETRIEVE_TIMEOUT = 15000;
/**
 * Drives the multi-domain OAuth handshake and exposes the resulting token to the
 * rest of the app through the shared `accesstoken` storage key, so an OAuth login
 * is indistinguishable from a regular email/password login downstream.
 */
export class OauthService extends ApiService {
    /**
     * Step 1 + 2: mint a correlation UUID, persist it as a first-party cookie so it
     * survives the redirect round-trip, then send the top-level page to the auth
     * selector with our return URL and the UUID as `state`.
     */
    beginSignIn() {
        const state = crypto.randomUUID();
        Cookies.set(OAUTH_STATE_COOKIE, state, {
            domain: WebUtils.getCookieDomain(),
            sameSite: "Lax" /* COOKIES_ATTRIBUTES.SAME_SITE_LAX */
        });
        const signInUrl = UrlUtils.getUrlWithQueries(URLS.OAUTH_SIGN_IN_PAGE, {
            [REDIRECT_URI_PARAM]: this.buildReturnUrl(),
            [STATE_PARAM]: state
        });
        WebUtils.redirectToSpecificUrl(signInUrl);
    }
    /** True when the current page load is the redirect back from the auth domain. */
    isReturningFromAuth() {
        return UrlUtils.getQueryValueFromUrl(OAUTH_CALLBACK_PARAM) === OAUTH_CALLBACK_VALUE;
    }
    /**
     * Step 3 + 4: exchange the stored `state` for the token minted on the auth
     * domain by loading its token page in a hidden iframe and awaiting the token
     * via postMessage. Persists the token as `accesstoken` on success.
     */
    completeSignIn() {
        const state = Cookies.get(OAUTH_STATE_COOKIE);
        this.clearReturnMarker();
        if (!state) {
            return Promise.reject(new Error('Missing OAuth state cookie'));
        }
        return this.retrieveToken(state).then(token => {
            Cookies.remove(OAUTH_STATE_COOKIE, { domain: WebUtils.getCookieDomain() });
            this.storeToken(token);
        });
    }
    /** Resolve the currently logged-in user from the stored token, if any. */
    getSession() {
        if (!window.localStorage.getItem(ACCESS_TOKEN_KEY)) {
            return Promise.resolve({ user: null });
        }
        const url = UrlUtils.getUrlWithQueries(URLS.USER_VERIFY_TOKEN, { lang: L10nUtils.getCurrentLanguage() });
        return this.get(url)
            .then(resp => resp.json())
            .then(data => { var _a; return ({ user: (_a = data === null || data === void 0 ? void 0 : data.user) !== null && _a !== void 0 ? _a : null }); });
    }
    /** Clear the local token so the app treats the user as signed out. */
    signOut() {
        window.localStorage.removeItem(ACCESS_TOKEN_KEY);
        Cookies.remove(OAUTH_STATE_COOKIE, { domain: WebUtils.getCookieDomain() });
        this.notifyTokenChange(null);
    }
    retrieveToken(state) {
        return new Promise((resolve, reject) => {
            const iframe = document.createElement('iframe');
            iframe.setAttribute('aria-hidden', 'true');
            iframe.style.display = 'none';
            iframe.src = UrlUtils.getUrlWithQueries(URLS.OAUTH_TOKEN_PAGE, { [STATE_PARAM]: state });
            let timer = null;
            const cleanup = () => {
                if (timer !== null) {
                    window.clearTimeout(timer);
                }
                window.removeEventListener('message', onMessage);
                iframe.remove();
            };
            const onMessage = (event) => {
                if (event.origin !== URLS.OAUTH_AUTH_ORIGIN) {
                    return;
                }
                const data = event.data;
                if (!data || data.type !== TOKEN_MESSAGE_TYPE) {
                    return;
                }
                cleanup();
                data.token ? resolve(data.token) : reject(new Error(data.error || 'OAuth token relay failed'));
            };
            timer = window.setTimeout(() => {
                cleanup();
                reject(new Error('OAuth token relay timed out'));
            }, TOKEN_RETRIEVE_TIMEOUT);
            window.addEventListener('message', onMessage);
            document.body.appendChild(iframe);
        });
    }
    storeToken(token) {
        window.localStorage.setItem(ACCESS_TOKEN_KEY, token);
        this.notifyTokenChange(token);
    }
    /**
     * Same-tab `localStorage` writes don't fire the native `storage` event, so
     * replay one manually to let the main Account overlay (which already listens
     * for it) verify the token and log the whole app in.
     */
    notifyTokenChange(token) {
        window.dispatchEvent(new StorageEvent('storage', {
            key: ACCESS_TOKEN_KEY,
            newValue: token,
            storageArea: window.localStorage
        }));
    }
    buildReturnUrl() {
        const url = new URL(window.location.href);
        url.searchParams.set(OAUTH_CALLBACK_PARAM, OAUTH_CALLBACK_VALUE);
        return url.toString();
    }
    clearReturnMarker() {
        const url = new URL(window.location.href);
        url.searchParams.delete(OAUTH_CALLBACK_PARAM);
        window.history.replaceState(window.history.state, '', url.toString());
    }
}
